import NodeType from 'types/transformation/enums/NodeType'

/**
 * Design tokens for the transformation board.
 *
 * Every node type gets a fixed accent (chip background, selection-ring tint
 * and a strong tone for icons, borders and handles). The hexes are fixed on
 * purpose — the theme's secondary/tertiary colors can be overridden per
 * customer via profile_colors, which would break the board's palette.
 */
export interface NodeAccent {
  /** Icon-chip background ("bakgrund" tone) */
  bg: string
  /** Selection-ring tint */
  soft: string
  /** Icon, selected border and handle color ("stark" tone) */
  stark: string
}

export const BOARD = {
  border: '#E4E4E7',
  borderSoft: '#EFEFF1',
  rowBackground: '#F6F6F8',
  chipBackground: '#F4F4F5',
  canvas: '#F7F7F9',
  dots: '#D8D8DE',
  edge: '#94A3B8',
  edgeSelected: '#475569',
  signalGreen: '#22C55E',
  signalGreenDark: '#16A34A',
  textPrimary: '#1A1A1D',
  textSecondary: '#3F3F46',
  textMuted: '#71717A',
  textFaint: '#A1A1AA',
  shadowRest: '0 1px 2px rgba(16, 24, 40, 0.05)',
  shadowLifted: '0 10px 24px rgba(16, 24, 40, 0.10)',
  fontDisplay: '\'DM Sans\', sans-serif',
  fontBody: '\'Source Sans 3\', \'Helvetica Neue\', sans-serif'
} as const

const GREEN: NodeAccent = { bg: '#BBF7D0', soft: '#DCFCE7', stark: '#16A34A' }
const LILA: NodeAccent = { bg: '#E9D5FF', soft: '#F3E8FF', stark: '#9333EA' }
const BLUE: NodeAccent = { bg: '#BFDBFE', soft: '#DBEAFE', stark: '#2563EB' }
const LIME: NodeAccent = { bg: '#D9F99D', soft: '#ECFCCB', stark: '#65A30D' }
const DARK: NodeAccent = { bg: '#E4E4E7', soft: '#F4F4F5', stark: '#1A1A1D' }
const CYAN: NodeAccent = { bg: '#A5F3FC', soft: '#CFFAFE', stark: '#0891B2' }
const TEAL: NodeAccent = { bg: '#99F6E4', soft: '#CCFBF1', stark: '#0D9488' }
const EMERALD: NodeAccent = { bg: '#A7F3D0', soft: '#D1FAE5', stark: '#059669' }
const INDIGO: NodeAccent = { bg: '#C7D2FE', soft: '#E0E7FF', stark: '#4F46E5' }
const FUCHSIA: NodeAccent = { bg: '#F5D0FE', soft: '#FAE8FF', stark: '#C026D3' }
const PINK: NodeAccent = { bg: '#FBCFE8', soft: '#FCE7F3', stark: '#DB2777' }
const SLATE: NodeAccent = { bg: '#E2E8F0', soft: '#F1F5F9', stark: '#475569' }

const NODE_ACCENTS: Partial<Record<NodeType, NodeAccent>> = {
  [NodeType.INPUT]: GREEN,
  [NodeType.LEFT_JOIN]: LILA,
  [NodeType.UNITE]: LILA,
  [NodeType.FILTER]: BLUE,
  [NodeType.AGGREGATE]: LIME,
  [NodeType.MAP]: DARK,
  [NodeType.DATE_PARSER]: CYAN,
  [NodeType.CAST]: TEAL,
  [NodeType.CONCATENATE]: EMERALD,
  [NodeType.CALCULATE]: INDIGO,
  [NodeType.REPLACE]: FUCHSIA,
  [NodeType.TEXT_REPLACE]: PINK,
  [NodeType.TIME_DIFFERENCE]: BLUE
}

export function getAccent(type: NodeType): NodeAccent {
  return NODE_ACCENTS[type] ?? SLATE
}
